import * as Api from '@ViewModels';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { useActionItemStepState } from '../../hooks/useActionItemStepState';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { ActionItemStepForm } from '../../components/ActionItemStepForm';
import { ActionItemAdvancedOptions } from '../../components/ActionItemAdvancedOptions';
import { ScheduleEditor } from '../../components/ScheduleEditor';
import { useStepRenderer } from './hooks/useStepRenderer';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';

interface ActionItemStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

type IActionItemAutomationStepWithNullableSchedule = Api.IActionItemAutomationStep & {
	schedule?: Api.IAutomationStepSchedule | null;
};

export function ActionItemStepRenderer({ nodeId, data, readOnly: readOnlyProp }: ActionItemStepRendererProps) {
	const {
		step: actionItemStep,
		template,
		canEdit,
		readonly,
		trigger,
		errorMessages,
		stepData,
		stepId,
		impersonationContext,
	} = useStepRenderer<IActionItemAutomationStepWithNullableSchedule>(data, readOnlyProp);

	const { updateNodeData, connectionTypes } = useAutomationFlowContext();
	const stepType = data.stepType;
	// Action items can intentionally have a null schedule to represent "No due date".
	// Use the step's value (even if null) once we have a step instance.
	const schedule = actionItemStep?.schedule ?? stepData?.schedule;

	const {
		actionItemContentEditorState,
		setActionItemContentEditorState,
		skipStepContactFilter,
		setSkipStepContactFilter,
		showRequiredFieldError,
		advancedOptionsToggled,
		setAdvancedOptionsToggled,
		autoSave,
		clearInlineErrorMessage,
		save,
	} = useActionItemStepState({
		actionItemStep,
		template,
		stepId: stepId || '',
		readonly,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const syncStepToNodeData = (updatedFields: Partial<IActionItemAutomationStepWithNullableSchedule>) => {
		if (!updateNodeData || !actionItemStep) return;
		updateNodeData(nodeId, {
			automationStep: { ...actionItemStep, ...updatedFields },
		});
	};

	const onActionItemContentEditorStateChanged = (newState: Api.IRichContentEditorState) => {
		setActionItemContentEditorState(newState);
		clearInlineErrorMessage();

		if (!updateNodeData) {
			return;
		}

		const content = newState.getRawRichTextContent();
		const actionItemTitle = newState.getPlainTextPreview(false).trim();
		const label = actionItemTitle ? `Action Item: ${actionItemTitle}` : 'Action Item';

		// Keep node header/compact title in sync while typing.
		updateNodeData(nodeId, {
			...(actionItemStep
				? {
						automationStep: {
							...actionItemStep,
							content,
						},
					}
				: {}),
			stepData: {
				name: actionItemTitle,
				hasContent: newState.hasContent(),
			},
			label,
		});
	};

	const saveSenderOptions = (option: Api.ISendFromOptions | null, _sendToSender?: boolean) => {
		save({ newSendFrom: option });
		syncStepToNodeData({ sendFromOptions: option });
	};

	const handleFilterChange = (filter: Api.IContactsFilterRequest | null) => {
		setSkipStepContactFilter(filter);
		save({ newContactFilter: filter });
		syncStepToNodeData({ contactFilter: filter ?? undefined });
	};

	const handleScheduleChange = (newSchedule: Api.IAutomationStepSchedule | null) => {
		save({ newSchedule });
		syncStepToNodeData({ schedule: newSchedule });
	};

	const hasContent = !!actionItemContentEditorState?.hasContent();
	const baseKey = `${data.stepType}-${nodeId}-${actionItemStep?.id || ''}`;

	return (
		<>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='simple'
				retryCount={autoSave.retryCount}
				errorMessage={autoSave.errorMessage}
			/>
			{actionItemStep && (
				<ScheduleEditor
					schedule={schedule}
					trigger={trigger}
					stepType={stepType}
					canEdit={canEdit}
					isBusy={autoSave.saveStatus === 'saving'}
					step={actionItemStep}
					index={0}
					onScheduleChange={handleScheduleChange}
					parentCaseInfo={data.parentCaseInfo}
				/>
			)}
			<ActionItemStepForm
				actionItemContentEditorState={actionItemContentEditorState}
				hasContent={hasContent}
				readonly={readonly}
				showRequiredFieldError={showRequiredFieldError}
				baseKey={baseKey}
				onContentStateChanged={onActionItemContentEditorStateChanged}
				onSave={() => {
					save();
					syncStepToNodeData({ content: actionItemContentEditorState?.getRawRichTextContent() });
				}}
				onClearError={clearInlineErrorMessage}
			/>
			<ActionItemAdvancedOptions
				actionItemStep={actionItemStep}
				nodeId={nodeId}
				stepId={stepId || nodeId}
				skipStepContactFilter={skipStepContactFilter}
				canEdit={canEdit}
				isCollapsed={!advancedOptionsToggled}
				onToggle={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
				onSaveSenderOptions={saveSenderOptions}
				onFilterChange={handleFilterChange}
				connectionTypes={connectionTypes}
				impersonationContext={impersonationContext}
			/>
		</>
	);
}
